import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Contributions / New Money → Contribution (One-Time) / Contribution (Recurring).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Contribution (One-Time)", "One-Time
 * Contribution (Qualified)", "One-Time Contribution (Non-Qualified)",
 * "Contribution (Recurring)", "Recurring Contribution (Qualified)" and
 * "Recurring Contribution (Non-Qualified)". One-Time and Recurring ask the
 * same funding and Move Money questions; Recurring moves the amount into a
 * schedule panel on the second screen. Kept parallel with Distributions.js.
 * Shared chrome and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Contributions / New Money',
    subCategory: 'Contribution (One-Time) or Contribution (Recurring)',
    subject: subjectRow('One-time contribution - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>One-Time and Recurring ask almost the same questions.</strong> The differences are called out
        in the reference below - mainly, Recurring asks for the amount and schedule on the second screen
        instead of the first.
      </>
    ),
  }),
  {
    title: 'Answer the Contribution Details questions',
    body: (
      <p style={{ margin: 0 }}>
        The <strong>Contribution (One-Time)</strong> or <strong>Contribution (Recurring)</strong> screen asks
        about the money, how it is arriving and whether there is a Move Money on file. Questions reveal
        themselves one at a time based on your answers, and every question in the reference below is required
        once it appears. Your answer to <em>Is this for a Qualified or Non-Qualified Account?</em> decides which
        screen comes next.
      </p>
    ),
  },
  {
    title: 'Describe the contribution and choose the account',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          <strong>One-Time:</strong> the next screen opens with a required box, <em>Please add any additional
          instructions relevant to this Case.</em> (2,000 characters max). It becomes the case description
          Operations reads first. Below it, pick one row in the <strong>Qualified Accounts</strong> or{' '}
          <strong>Non-Qualified Accounts</strong> table - the account the money is going into.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Recurring:</strong> the next screen has a recurring schedule panel - the contribution amount,
          how often and from when (with an optional end), and a details box that becomes the case description -
          plus the same <strong>Qualified Accounts</strong> or <strong>Non-Qualified Accounts</strong> table.
          Pick one row.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the table (Recurring adds a <em>Recurring
        Contributions</em> authorization - see What happens next). Select <em>I have read, understand, and
        agree to the above information</em> under Acknowledgement and click Next. FSC creates the case and opens
        it. Note the case number for follow-up.
      </p>
    ),
  },
];

const MOVE_MONEY_OPTIONS = [
  'Yes (There is a Move Money on file)',
  'No (There is not a Move Money on file, I am adding one to use with this Case)',
  'Not Applicable (I understand if a Move Money is required but not on file it will result in a NIGO)',
];

const DIRECTION_OPTIONS = [
  'Setup ACH In (From Bank Account TO Account with Prosperity)',
  'Setup ACH OUT (From Prosperity Account TO Bank Account)',
  'Setup ACH IN and OUT (To/From Prosperity, To/From Bank Account)',
];

const BANKING_NOTE = (ownerLabels) => (
  <>
    A <strong>Banking Information</strong> section asks for: <em>Name of Banking Institution</em>,{' '}
    <em>Bank Account Type (Individual, Joint, Trust, etc...)</em>, {ownerLabels},{' '}
    <em>Bank Account Number</em>, <em>Bank Account Routing Number</em> and the bank&rsquo;s address. If you
    said there is more than one person on the account, it also asks for <em>Joint First Name (Bank Account
    Owner / Name on Account)</em> and <em>Joint Last Name (Bank Account Owner / Name on Account)</em>. All are
    required. FSC saves these as new banking instructions linked to the case.
  </>
);

const QUESTIONS = [
  // One-Time, first screen
  {
    screen: 'Contribution (One-Time)',
    q: 'One Time Contribution Amount',
    when: 'Always - this is the first question',
    note: 'The next question only appears once the amount is more than $1.',
  },
  {
    q: 'What is the Source of Funds?',
    when: 'Once the amount is entered',
  },
  {
    q: 'Is there more than one person on the account? (Joint Owner, Trustee, POA, etc...)',
    when: 'Once the source of funds is entered',
    options: ['Yes (There is more than one person on the account)', 'No (There is not more than one person on the account)'],
    note: 'Yes adds joint-owner name fields to the Banking Information section if you add a new Move Money.',
  },
  {
    q: 'Is this for a Qualified or Non-Qualified Account?',
    when: 'After the joint-owner question',
    options: ['Qualified', 'Non-Qualified'],
    note: 'Decides whether the next screen lists Qualified or Non-Qualified accounts.',
  },
  {
    q: 'Please select the Tax Year',
    when: 'Only if you chose Qualified',
    options: ['2025', '2026'],
  },
  {
    q: 'Qualified Contribution Type',
    when: 'Only for Qualified, once the tax year is chosen',
    options: ['Regular', 'Rollover', 'Transfer', 'Employer'],
  },
  {
    q: 'Expected Date',
    when: 'For Non-Qualified straight away; for Qualified once the contribution type is chosen',
  },
  {
    q: 'Do you need Trading to place trades when the funds arrive to this account? (Please note, if this is an SMA, the Trading/Operations Team will forward this request to the SMA manager)',
    when: 'Once the expected date is entered',
    options: [
      'Yes (Please have trading rebalance the cash to the chosen Sleeve)',
      'No (We will let you know when we confirm with the Advisor/Client, we acknowledge that funds will not be traded until then and that we will need to reach out to the Prosperity Operations team when circumstances have changed)',
    ],
    note: 'Yes makes FSC create a Trading task from the case automatically when you submit. With No, the cash is not traded until you contact Operations.',
  },
  {
    q: 'How are the funds arriving?',
    when: 'Once the Trading question is answered',
    options: ['ACH', 'Wire', 'Check', 'Other'],
  },
  {
    q: 'Is there a Move Money on file?',
    when: 'Once you have said how the funds are arriving',
    options: MOVE_MONEY_OPTIONS,
    note: 'Not Applicable lets you continue, but if a Move Money is needed and not on file the request is returned NIGO.',
    warn: true,
  },
  {
    q: 'Is there a Move Money on file? (If using a Bank thats on file, please provide the Bank name, If sending by Check or Wire, please enter the details here)',
    when: 'Only if you answered Yes or Not Applicable above',
    note: 'A free-text box - name the bank on file, or give the check or wire details.',
  },
  {
    q: 'Which Direction(s) Should Funds be allowed to Move?',
    when: 'Only if you are adding a new Move Money (the No answer above)',
    options: DIRECTION_OPTIONS,
    note: BANKING_NOTE(<><em>Bank Account Owner / Name on Account First Name</em>, <em>Bank Account Owner / Name on Account Last Name</em></>),
  },
  {
    screen: 'One-Time Contribution (Qualified) / (Non-Qualified)',
    q: 'Please add any additional instructions relevant to this Case.',
    when: 'Always - the first box on the second screen',
    note: '2,000 characters max. This becomes the case description.',
  },
  {
    q: 'Qualified Accounts / Non-Qualified Accounts (table)',
    when: 'Always on the second screen - you see the table matching your Qualified / Non-Qualified answer',
    note: 'Pick exactly one row - the sleeve the money is going into. Columns: Name, Account Details (Non-Qualified) or Account Information (Qualified), Model, Cash Balance and Balance.',
  },
  // Recurring, first screen
  {
    screen: 'Contribution (Recurring)',
    q: 'What is the Source of Funds?',
    when: 'Always - this is the first question (there is no amount on this screen)',
  },
  {
    q: 'Is there more than one person on the account? (Joint Owner, Trustee, POA, etc...)',
    when: 'Once the source of funds is entered',
    options: ['Yes (There is more than one person on the account)', 'No (There is not more than one person on the account)'],
  },
  {
    q: 'When do you expect the funds to arrive?',
    when: 'After the joint-owner question',
  },
  {
    q: 'How are the funds arriving?',
    when: 'Once the date is entered',
    options: ['ACH', 'Wire', 'Check', 'Other'],
  },
  {
    q: 'Is this for a Qualified or Non-Qualified Account?',
    when: 'Once you have said how the funds are arriving',
    options: ['Qualified', 'Non-Qualified'],
  },
  {
    q: 'Please select the Tax Year · Qualified Contribution Type',
    when: 'Only if you chose Qualified - same options as One-Time',
    options: ['2025', '2026', 'Regular', 'Rollover', 'Transfer', 'Employer'],
  },
  {
    q: 'Do you need Trading to raise cash each month? (Please note, if this is for an SMA, the Trading Team will forward this request to the SMA Manager)',
    when: 'For Non-Qualified straight away; for Qualified once the contribution type is chosen',
    options: [
      'Yes (We authorize Trading to buy the contribution proceeds to the underlying strategy each cycle)',
      'No (We will let you know each time. We acknowledge it is our responsibility to reach out to the Trading Team each month to facilitate the Trades)',
    ],
    note: 'The Yes answer authorizes Trading to buy with each contribution, and makes FSC create a Trading task from the case automatically when you submit.',
  },
  {
    q: 'Is there a Move Money on file? (If using a Bank thats on file, please provide the Bank Name if there is more than one)',
    when: 'Once the Trading question is answered',
    options: MOVE_MONEY_OPTIONS,
    note: 'Not Applicable lets you continue, but if a Move Money is needed and not on file the request is returned NIGO.',
    warn: true,
  },
  {
    q: 'Funding Information (If using a Bank thats on file, please provide the Bank Name if there is more than one)',
    when: 'Only if you answered Yes or Not Applicable above',
  },
  {
    q: 'Which Direction(s) Should Funds be allowed to Move?',
    when: 'Only if you are adding a new Move Money (the No answer above)',
    options: DIRECTION_OPTIONS,
    note: BANKING_NOTE(<><em>First Name (Bank Account Owner / Name on Account)</em>, <em>Last Name (Bank Account Owner / Name on Account)</em></>),
  },
  {
    screen: 'Recurring Contribution (Qualified) / (Non-Qualified)',
    q: 'Recurring schedule panel',
    when: 'Always on the second screen',
    note: 'Enter the contribution amount, the schedule (how often, and on which day) with a start date and an optional end date, and the details. The details become the case description; with no end date the schedule runs until cancelled.',
  },
  {
    q: 'Qualified Accounts / Non-Qualified Accounts (table)',
    when: 'Always on the second screen - you see the table matching your Qualified / Non-Qualified answer',
    note: 'Pick exactly one row - the sleeve the contributions go into. Columns: Name, Account Information, Model, Cash Balance and Balance.',
  },
];

const RECURRING_NOTE = (
  <Callout>
    <strong>Recurring contributions.</strong> The Recurring screens add a <em>Recurring Contributions</em>{' '}
    authorization: by submitting, you authorize Operations to process the contribution on the stated schedule
    until it is cancelled. It keeps running until an Advisor or CSA contacts Operations to change or stop it,
    returned or failed items can interrupt the schedule, and the submitting CSA / Advisor is responsible for
    confirming eligibility and limits (e.g., IRA contribution limits). FSC records the schedule against the
    selected sleeve and links it to the case.
  </Callout>
);

const TRADING_NOTE = (
  <Para>
    If you answered Yes to the Trading question, FSC also creates a Trading task from the case as soon as
    it is submitted.
  </Para>
);

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Contributions / New Money'),
  {
    h: 'The questions stopped after the amount',
    p: ['On One-Time, the rest of the screen only appears once One Time Contribution Amount is more than $1.'],
  },
  {
    h: 'The account table is empty, or the account I expected is missing',
    p: [
      'The Qualified Accounts table only lists qualified sleeves, and Non-Qualified Accounts only non-qualified ones - if the account is on the other side, go Back and change your answer to Is this for a Qualified or Non-Qualified Account?',
      'Neither table lists the custodial account row itself (the account number with no sleeve suffix) or the holding sleeves whose names end in Contribution or Distribution - pick the model sleeve the money should land in.',
    ],
  },
  troubleSleeveRows('Inactive sleeves, sleeves with a negative balance, and cancelled sleeves ending in CNX are filtered out of both tables.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function Contributions() {
  return (
    <GuidePage
      title="Contribution Requests"
      icon="box-arrow-in-down"
      path="Operations → Contributions / New Money → Contribution (One-Time) or Contribution (Recurring)"
      minutes={5}
      intro="How to tell the Prosperity Operations team about new money coming into a client account - once or on a schedule - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and some paths need all of them.</Para>
        <Bullets items={[
          <><strong>The account the money is going into</strong>, and whether it is qualified or non-qualified. You pick the sleeve from a list, so you do not need account numbers memorised.</>,
          <><strong>The amount</strong> and <strong>the source of funds</strong>. For Recurring, also the schedule - how often, the start date and any end date.</>,
          <><strong>When and how the money is arriving</strong> - ACH, wire, check or other.</>,
          <><strong>For a qualified account</strong>, the tax year and the contribution type (Regular, Rollover, Transfer or Employer).</>,
          <><strong>Whether Trading should invest the cash</strong> when it arrives.</>,
          <><strong>The Move Money position.</strong> If one is on file, the bank name. If you are adding one, the bank name, account type, owner name(s), account and routing numbers and the bank&rsquo;s address.</>,
        ]} />
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up. One-Time questions come first, then Recurring.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" before={<>{TRADING_NOTE}{RECURRING_NOTE}</>} />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not cover contribution eligibility or limits, which
        remain the submitting CSA / Advisor&rsquo;s responsibility.
      </Footnote>
    </GuidePage>
  );
}
